import React, { useEffect, useMemo, useRef, useState } from "react";
import { Modal, ModalHeader, ModalBody, CardFooter, Button } from "reactstrap";
import { useQueryClient, useMutation } from "@tanstack/react-query";
import { toast } from "react-toastify";
import { FormattedMessage, useIntl } from "react-intl";
import ConfirmDialog from "../Modal/ConfirmDialog";
import { formatServiceError } from "../../Utilities/serviceErrorHelper";
import { GetAvailableStudentsForPeriod } from "../../actions/teacherQueries";
import { ChangeStudentsAndSupervisorsInPeriod } from "../../actions/teacherMutations";
import CreateAPLPeriodSupervisor from "./CreateAPLPeriodSupervisor";

const transformStudentsWithSupervisors = (periodStudents) =>
  periodStudents.map((stud) => ({
    ...stud.student,
    companyId: stud.companyId,
    studentId: stud.student.id,
    supervisors: stud.supervisors.map((studsup) => ({
      ...studsup.supervisor,
      isMainResponsible: studsup.isMainResponsible,
    })),
  }));

const getDraftSignature = (list) =>
  JSON.stringify(
    [...list]
      .sort((a, b) => String(a.id).localeCompare(String(b.id)))
      .map((s) => ({
        id: s.id,
        companyId: s.companyId ?? null,
        supervisors: [...(s.supervisors || [])]
          .sort((a, b) => String(a.id).localeCompare(String(b.id)))
          .map((sv) => ({
            id: sv.id,
            isMainResponsible: !!sv.isMainResponsible,
          })),
      })),
  );

const ModifySupervisorsModal = ({
  allSupervisors,
  allStudents,
  isOpen,
  toggle,
  period,
  listOfStudentIds,
  schoolId,
  level,
  objId,
  programTeachers,
  teacherInfo,
}) => {
  const intl = useIntl();
  const [newStudentsAndSupervisors, setNewStudentsAndSupervisors] = useState(
    []
  );
  const [selectableStudents, setSelectableStudents] = useState([]);
  const [promptRemoveStudents, setPromptRemoveStudents] = useState(false);
  const [studentsForRemoval, setStudentsForRemoval] = useState([]);
  const [postButtonDisabled, setPostButtonDisabled] = useState(false);
  const [confirmDiscardOpen, setConfirmDiscardOpen] = useState(false);
  const [draftInitialized, setDraftInitialized] = useState(false);
  const [draftResetKey, setDraftResetKey] = useState(0);

  const initialRef = useRef([]);

  const isDirty = useMemo(() => {
    if (!draftInitialized) return false;
    return (
      getDraftSignature(newStudentsAndSupervisors) !==
      getDraftSignature(initialRef.current)
    );
  }, [draftInitialized, newStudentsAndSupervisors]);

  const requestClose = () => {
    if (isDirty) {
      setConfirmDiscardOpen(true);
    } else {
      toggle();
    }
  };

  const handleDiscardAndClose = () => {
    setNewStudentsAndSupervisors(initialRef.current);
    setStudentsForRemoval([]);
    setPromptRemoveStudents(false);
    setConfirmDiscardOpen(false);
    setDraftResetKey((prev) => prev + 1);
    toggle();
  };

  const mutateChangeStudentsAndSupervisorsInPeriod = useMutation({
    mutationFn: ChangeStudentsAndSupervisorsInPeriod,
  });

  const availableStudentIds = GetAvailableStudentsForPeriod({
    candidates: {
      periodId: period.period.id,
      candidateStudentIds: listOfStudentIds,
    },
  });

  const queryClient = useQueryClient();

  useEffect(() => {
    if (!isOpen) {
      setConfirmDiscardOpen(false);
      setDraftInitialized(false);
    }
  }, [isOpen]);

  useEffect(() => {
    if (draftInitialized) return;
    if (
      isOpen &&
      period?.period &&
      availableStudentIds.data &&
      !availableStudentIds.isPending &&
      !availableStudentIds.error
    ) {
      const tempNewStudentsAndSupervisors = transformStudentsWithSupervisors(
        period.period.students,
      );
      initialRef.current = tempNewStudentsAndSupervisors;
      setNewStudentsAndSupervisors(tempNewStudentsAndSupervisors);
      setDraftInitialized(true);
      setDraftResetKey((prev) => prev + 1);

      const availableStudentIdSet = new Set([
        ...availableStudentIds.data,
        ...tempNewStudentsAndSupervisors.map((s) => s.id),
      ]);
      const tempAvailableStudents = allStudents.filter((stud) =>
        availableStudentIdSet.has(stud.id),
      );

      const seenIds = new Set();
      const selectableStudentsList = tempAvailableStudents
        .filter((s) => {
          if (seenIds.has(s.student.id)) return false;
          seenIds.add(s.student.id);
          return true;
        })
        .map((s) => ({ ...s.student }));
      setSelectableStudents(selectableStudentsList);
    }
  }, [
    isOpen,
    draftInitialized,
    period,
    availableStudentIds.isPending,
    availableStudentIds.error,
    availableStudentIds.data,
    allStudents,
  ]);

  const saveChanges = () => {
    const allStudentsIncluded = period.period.students.every((oldStud) =>
      newStudentsAndSupervisors.some(
        (currStud) => currStud.id === oldStud.studentId
      )
    );
    if (allStudentsIncluded) {
      handlePostChanges();
    } else {
      const tempStudentsForRemoval = [];
      period.period.students.forEach((oldStud) => {
        if (
          !newStudentsAndSupervisors.some(
            (currStud) => currStud.id === oldStud.studentId
          )
        ) {
          tempStudentsForRemoval.push(oldStud);
        }
      });
      setStudentsForRemoval(tempStudentsForRemoval);
      setPromptRemoveStudents(true);
    }
  };

  const handlePostChanges = () => {
    setStudentsForRemoval([]);
    setPromptRemoveStudents(false);
    setPostButtonDisabled(true);
    const studentsToPost = [];
    newStudentsAndSupervisors.forEach((stud) => {
      let studentToAdd = {
        name: stud.name,
        companyId: stud.companyId,
        studentId: stud.id,
        supervisors: stud.supervisors,
      };
      if (studentToAdd.supervisors.length === 0) {
        studentToAdd.supervisors.push({
          ...teacherInfo.data,
          IsMainResponsible: true,
        });
      }
      studentsToPost.push(studentToAdd);
    });

    mutateChangeStudentsAndSupervisorsInPeriod.mutate(
      {
        periodId: period.period.id,
        deleteMissing: true,
        students: studentsToPost,
        studentIdsToManage: listOfStudentIds,
      },
      {
        onSuccess: () => {
          setPostButtonDisabled(false);
          toast.success(<FormattedMessage id="PeriodUpdated" />);
          queryClient.invalidateQueries({
            queryKey: ["FetchPeriods", [level, objId]],
          });
          toggle();
        },
        onError: (err) => {
          setPostButtonDisabled(false);
          toast.warn(formatServiceError(intl, err?.response?.data));
        },
      },
    );
  };

  return (
    <>
      <Modal
        isOpen={isOpen}
        toggle={requestClose}
        size="lg"
        scrollable
        contentClassName="aplmod-modal-content"
      >
        <ModalHeader toggle={requestClose} className="aplmod-modal-header">
          <FormattedMessage id="ManageSupervisorsAndStudentsInPeriod" />
        </ModalHeader>
        <ModalBody className="aplmod-modal-body">
          <CreateAPLPeriodSupervisor
            key={draftResetKey}
            students={selectableStudents}
            supervisors={allSupervisors}
            aplStudentSupervisors={newStudentsAndSupervisors}
            setAplStudentSupervisors={setNewStudentsAndSupervisors}
            schoolId={schoolId}
            programTeachers={programTeachers}
          />
        </ModalBody>
        <CardFooter>
          <div className="d-flex justify-content-end m-3 gap-2">
            <Button outline color="secondary" onClick={requestClose}>
              <FormattedMessage id="Cancel" />
            </Button>
            <Button
              outline
              color="primary"
              onClick={() => saveChanges()}
              disabled={postButtonDisabled}
            >
              <FormattedMessage id="Save" />
            </Button>
          </div>
        </CardFooter>
      </Modal>
      <ConfirmDialog
        isOpen={promptRemoveStudents}
        toggle={() => setPromptRemoveStudents(!promptRemoveStudents)}
        title={<FormattedMessage id="AreYouSure" />}
        message={
          <FormattedMessage
            values={{
              students: studentsForRemoval.map((a) => " " + a.student.name),
            }}
            id="WillBeRemovedFromPeriod"
          />
        }
        onProceed={handlePostChanges}
        danger={true}
      />
      <ConfirmDialog
        isOpen={confirmDiscardOpen}
        toggle={() => setConfirmDiscardOpen(false)}
        title={<FormattedMessage id="DiscardChangesTitle" />}
        message={<FormattedMessage id="DiscardChangesMessage" />}
        cancelKey="ContinueEditing"
        proceedKey="DiscardChanges"
        onProceed={handleDiscardAndClose}
        danger={true}
      />
    </>
  );
};
export default ModifySupervisorsModal;
